import { JButton } from '@atoms/JButton';
import Jpaper from '@atoms/JPaper';
import { IconSize } from '@constants';
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import TopUpsItemDto from '@/modules/credits/models/dto/top-ups-item.dto';
import useTopUpsMutationContext from '@/modules/credits/hooks/useTopUpsMutationContext';
import { useCreateTopUps } from '@/modules/credits/queries';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useJSnackbar from '@/hooks/useJSnackbar';
import Paths from '@/routes/paths';
import useWebSocket from '@/hooks/useWebSocket';
import { NOTIFICATION_EVENTS } from '@services/endpoints';

type valuesProp = Partial<TopUpsItemDto>;

interface Props {
  isValid: boolean;
  values: valuesProp;
  submitTab?: boolean;
}

const TopUpsFooter = ({ isValid, values, submitTab }: Props) => {
  const { t } = useTranslation();
  const navigation = useNavigate();
  const { showSnackbar } = useJSnackbar();
  const { socket } = useWebSocket(NOTIFICATION_EVENTS);

  const { stepIndex, setStepIndex, mode, payload, setPayload } =
    useTopUpsMutationContext();

  const {
    mutate,
    isSuccess: isCreateSuccess,
    isError: isCreateError,
  } = useCreateTopUps();

  useEffect(() => {
    if (!isCreateSuccess) return;
    const enterpriseId = payload?.createCreditTopupDto?.enterpriseId ?? '';
    socket?.emit('newCreditTopup', { enterpriseId });
    navigation(Paths.CreditsPage);
    showSnackbar(t('TOP_UPS_CREATED_SUCCESSFULLY'), 'success');
  }, [isCreateSuccess]);

  useEffect(() => {
    if (isCreateError) {
      showSnackbar(t('API_ERROR'), 'error');
    }
  }, [isCreateError]);

  const onPreviousClick = (formValues: valuesProp) => {
    if (stepIndex === 0) return;
    setPayload({ ...payload, ...formValues });
    setStepIndex((prev) => (prev ? prev - 1 : 1));
  };

  const onNextClick = (formValues: valuesProp) => {
    if (!submitTab) {
      setPayload({ ...payload, ...formValues });
      setStepIndex((prev) => (prev ? prev + 1 : 1));
    } else {
      if (payload) {
        mutate({ ...payload, ...values });
      }
    }
  };
  return (
    <Jpaper direction="row" justifyContent="space-between" alignItems="center">
      <JButton
        disabled={stepIndex === 0}
        onClick={() => onPreviousClick(values)}
        startIcon={<ArrowLeftIcon height={IconSize.md} />}
        variant="text"
      >
        {t('PREVIOUS')}
      </JButton>
      <JButton
        type="button"
        disabled={!isValid}
        onClick={() => onNextClick(values)}
        endIcon={<ArrowRightIcon height={IconSize.md} />}
        variant="contained"
      >
        {submitTab && mode === 'create'
          ? t('CREATE_TOP_UPS')
          : submitTab && mode === 'edit'
          ? t('EDIT_TOP_UPS')
          : t('NEXT')}
      </JButton>
    </Jpaper>
  );
};

export default TopUpsFooter;
